import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  Form,
  NumberInput,
  Select,
  SelectItem,
} from "@heroui/react";
import Box from "/app/src/components/generic/components/box";
import { Bin, Material, StorageRule } from "/app/src/models";
import { useCallback } from "react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { storageRuleService } from "/app/src/services";
import { handlePromiseError } from "/app/src/helpers/api";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface FormValues {
  binId?: string;
  minStockPerBin?: number;
  maxStockPerBin?: number;
  requiredCapacity?: number;
  isDefaultBin?: number;
}

/**
 * Formats the form values into the expected payload structure.
 * @param materialId The ID of the material.
 * @param values The form values object containing bin and capacity fields.
 * @returns An object containing formatted form data.
 */
function formatForm(materialId: string, values: FormValues) {
  return {
    materialId,
    binId: values.binId,
    minStockPerBin: values.minStockPerBin,
    maxStockPerBin: values.maxStockPerBin,
    requiredCapacity: values.requiredCapacity,
    isDefaultBin: values.isDefaultBin,
  };
}

/**
 * Renders storage rules accordion with form for updating and deleting a storage rule.
 * @param {Object} props - Component props.
 * @param {StorageRule} props.storageRule - The storage rule object.
 * @param {string} [props.binName] - Optional name of the bin.
 * @param {Material} props.material - The material associated with the rule.
 * @param {Bin[]} props.bins - List of available bins.
 * @returns {JSX.Element} The StorageRules component.
 */
export default function StorageRules({
  storageRule,
  binName,
  material,
  bins,
}: {
  storageRule: StorageRule;
  binName?: string;
  material: Material;
  bins: Bin[];
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /**
   * Returns the toggle icon for accordion based on open state.
   * @param {Object} param0 - Function parameters.
   * @param {boolean} param0.isOpen - Accordion open state.
   * @returns {JSX.Element} Chevron icon component.
   */
  const toggleIndicatorIcon = useCallback(({ isOpen }: { isOpen: boolean }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  const { mutateAsync: updateStorageRule } = useMutation({
    mutationFn: (storageRule: StorageRule) => {
      return storageRuleService
        .updateSingle(storageRule.id, storageRule)
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["storage_rules", material?.id],
        (oldData: { storage_rules: StorageRule[] }) => {
          return {
            storage_rules: oldData.storage_rules.map((oldRule: StorageRule) => {
              return oldRule.id === data.storage_rule.id
                ? data.storage_rule
                : oldRule;
            }),
          };
        },
      );
    },
  });

  /**
   * Handles updating a storage rule by formatting form values and sending mutation.
   * @param {FormValues} updatedValues - The updated form values.
   * @returns {Promise<void>} Promise resolving when update completes.
   */
  const updateStorageRuleHandler = useCallback(
    async (updatedValues: FormValues) => {
      const formatted = formatForm(material.id, updatedValues);
      const payload = {
        id: storageRule.id,
        ...formatted,
      } as StorageRule;
      await updateStorageRule(payload);
    },
    [material.id, updateStorageRule, storageRule.id],
  );

  const { mutateAsync: deleteStorageRule } = useMutation({
    mutationFn: (storageRule: StorageRule) => {
      return storageRuleService
        .deleteSingle(storageRule.id)
        .then(handlePromiseError);
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ["storage_rules", material?.id],
        (oldData: { storage_rules: StorageRule[] }) => {
          return {
            storage_rules: oldData.storage_rules.filter(
              (oldRule: StorageRule) => {
                return oldRule.id !== storageRule.id;
              },
            ),
          };
        },
      );
      queryClient.invalidateQueries(["storage_rules", material.id]);
    },
  });

  /**
   * Handles deletion of the current storage rule.
   * @returns {void}
   */
  const deleteStorageRuleHandler = useCallback(() => {
    deleteStorageRule(storageRule);
  }, [deleteStorageRule, storageRule]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      binId: storageRule.binId ? String(storageRule.binId) : "",
      minStockPerBin: storageRule.minStockPerBin ?? undefined,
      maxStockPerBin: storageRule.maxStockPerBin ?? undefined,
      requiredCapacity: storageRule.requiredCapacity ?? undefined,
      isDefaultBin: storageRule.isDefaultBin ?? 0,
    },
  });

  return (
    <Box>
      <Accordion
        selectionMode="single"
        showDivider
        itemClasses={{ trigger: "py-0" }}
      >
        <AccordionItem
          key={storageRule.id}
          title={binName || String(storageRule.binId)}
          indicator={toggleIndicatorIcon}
        >
          <Form
            onSubmit={handleSubmit(async (values) => {
              await updateStorageRuleHandler(values);
              reset(values);
            })}
            className="w-full"
          >
            <div className="flex w-full items-center justify-end gap-2 mb-8">
              <Button type="submit" color="primary">
                {t("translation:save")}
              </Button>
              <Button color="danger" onPress={deleteStorageRuleHandler}>
                {t("translation:remove")}
              </Button>
            </div>
            <GridWrapper
              className="w-full items-start gap-6"
              responsive={{ sm: 1, md: 3, lg: 5 }}
            >
              <Controller
                control={control}
                name="binId"
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col">
                    <Select
                      size="md"
                      label={t("translation:bin")}
                      placeholder={t("translation:select_bin")}
                      isDisabled
                      labelPlacement="outside"
                      selectedKeys={field.value ? [field.value] : []}
                      onSelectionChange={(keys) => {
                        const val = Array.from(keys)[0];
                        field.onChange(val ? String(val) : undefined);
                      }}
                      isInvalid={Boolean(error)}
                    >
                      {bins.map((bin) => (
                        <SelectItem key={bin.id}>{bin.name}</SelectItem>
                      ))}
                    </Select>
                    <div className="h-5 mt-1">
                      {error && (
                        <p className="text-danger text-sm">
                          {t("translation:required")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="minStockPerBin"
                rules={{
                  required: true,
                  min: {
                    value: 1,
                    message: t("translation:min_stock_per_bin_validation"),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col">
                    <NumberInput
                      size="md"
                      label={t("translation:min_stock_level")}
                      labelPlacement="outside"
                      placeholder="0"
                      value={field.value ?? 0}
                      onValueChange={(value) => field.onChange(value ?? null)}
                      isInvalid={Boolean(error)}
                      min={1}
                    />
                    <div className="h-5 mt-1">
                      {error && (
                        <p className="text-danger text-sm">
                          {error.message || t("translation:required")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="maxStockPerBin"
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col">
                    <NumberInput
                      size="md"
                      label={t("translation:max_stock_level")}
                      labelPlacement="outside"
                      placeholder="0"
                      value={field.value ?? 0}
                      onValueChange={(value) => field.onChange(value ?? null)}
                      isInvalid={Boolean(error)}
                    />
                    <div className="h-5 mt-1">
                      {error && (
                        <p className="text-danger text-sm">
                          {t("translation:required")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="requiredCapacity"
                rules={{
                  required: true,
                  min: {
                    value: 1,
                    message: t("translation:required_capacity_min"),
                  },
                  max: {
                    value: 100,
                    message: t("translation:required_capacity_max"),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col">
                    <NumberInput
                      size="md"
                      label={t("translation:required_capacity")}
                      labelPlacement="outside"
                      placeholder="0"
                      value={field.value ?? 0}
                      onValueChange={(value) => field.onChange(value ?? null)}
                      isInvalid={Boolean(error)}
                      min={1}
                      max={100}
                    />
                    <div className="h-5 mt-1">
                      {error && (
                        <p className="text-danger text-sm">
                          {error.message || t("translation:required")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="isDefaultBin"
                render={({ field }) => (
                  <div className="flex flex-col">
                    <label className="text-[16px] mb-4 -mt-[6px]">
                      {t("translation:is_default_bin")}
                    </label>
                    <div>
                      <Checkbox
                        isSelected={Boolean(field.value)}
                        onChange={field.onChange}
                      />
                    </div>
                  </div>
                )}
              />
            </GridWrapper>
          </Form>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
